import type { ReactElement } from 'react';
import { Icon, type IconProps } from './Icon';
import type { ResourceKey } from '../lib/game';

/**
 * Resource glyphs.
 *
 * Each one is a flat, isometric-leaning reduction of the asset described in the
 * GDD's prompt table, so the 20px HUD icon and the full-size rendered asset read
 * as the same object. All strokes use currentColor; the small filled cores are
 * what give each glyph its "energy" read at HUD size.
 */

/** Titan-Legierung — stacked alloy ingots. */
export function TitanIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 14.5 7 12h10l3 2.5-3 2.5H7l-3-2.5Z" />
      <path d="M6.5 9.5 9 7.5h6l2.5 2L15 11.5H9L6.5 9.5Z" />
      <path d="M9 12v-.5M15 12v-.5" opacity={0.6} />
      <path d="M8 14.5h8" opacity={0.45} />
    </Icon>
  );
}

/** Kristall-Silikat — raw silicate crystal node. */
export function SilicateIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 2.5 17 9l-5 12.5L7 9l5-6.5Z" />
      <path d="M7 9h10" />
      <path d="M12 2.5V21.5" opacity={0.5} />
    </Icon>
  );
}

/** Isotop-3 — reinforced containment canister. */
export function IsotopeIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="7" y="3.5" width="10" height="17" rx="3" />
      <path d="M7 8h10M7 16h10" opacity={0.55} />
      <path d="M9.5 11.5c1 .9 1.8.9 2.5 0s1.5-.9 2.5 0v3.2c-1-.9-1.8-.9-2.5 0s-1.5.9-2.5 0v-3.2Z" fill="currentColor" stroke="none" opacity={0.85} />
      <path d="M10 3.5V2M14 3.5V2" />
    </Icon>
  );
}

/** Megawatt — plasma sphere trapped in a magnetic ring. */
export function PowerIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="4" />
      <circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none" />
      <path d="M3.5 12c0 2 3.8 3.6 8.5 3.6s8.5-1.6 8.5-3.6S16.7 8.4 12 8.4 3.5 10 3.5 12Z" opacity={0.7} />
    </Icon>
  );
}

/** Aether-Masse — swirling gravitational anomaly. */
export function AetherIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="8.2" opacity={0.45} />
      <path d="M12 4.5c3.6 1.4 5.6 4.2 5.6 7.5s-2 6.1-5.6 7.5c-3.6-1.4-5.6-4.2-5.6-7.5S8.4 5.9 12 4.5Z" />
      <circle cx="12" cy="12" r="2.4" fill="currentColor" stroke="none" />
    </Icon>
  );
}

/** Galaktische Credits — credit chip with circuit traces. */
export function CreditIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3" y="5.5" width="18" height="13" rx="2.5" />
      <path d="M7 5.5v13M17 5.5v13" opacity={0.5} />
      <path d="M10 10h4v4h-4z" />
      <path d="M3 10h4M17 10h4M3 14h4M17 14h4" opacity={0.7} />
    </Icon>
  );
}

/** Chronos-Kristalle — prismatic premium crystal cluster. */
export function ChronosIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 2.5 16.5 8 12 21 7.5 8 12 2.5Z" />
      <path d="M7.5 8h9" opacity={0.6} />
      <path d="m5 11.5-1.8 2.2L5 17M19 11.5l1.8 2.2L19 17" opacity={0.65} />
    </Icon>
  );
}

/** Lookup used by <ResourceIcon resource="titan" />. */
export const RESOURCE_ICONS: Record<ResourceKey, (props: IconProps) => ReactElement> = {
  titan: TitanIcon,
  silicate: SilicateIcon,
  isotope: IsotopeIcon,
  power: PowerIcon,
  aether: AetherIcon,
  credit: CreditIcon,
  chronos: ChronosIcon,
};
